import React, { useMemo, useRef, useState } from 'react';
import { computeNiceTicks, modalityDisplayName } from './chartUtils';

export interface VerticalBarItem {
  label: string;
  value: number;
}

interface AnalyticsVerticalBarChartProps {
  title: string;
  data: VerticalBarItem[];
}

interface TooltipState {
  label: string;
  displayLabel: string;
  value: number;
  x: number;
}

export default function AnalyticsVerticalBarChart({
  title,
  data,
}: Readonly<AnalyticsVerticalBarChartProps>) {
  const plotRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const maxValue = Math.max(...data.map(d => d.value), 0);
  const yMax = Math.max(maxValue, 1);
  const yTicks = useMemo(() => computeNiceTicks(yMax, 4), [yMax]);
  const tickMax = yTicks[0] || yMax;

  const showTooltip = (
    item: VerticalBarItem,
    displayLabel: string,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    const plot = plotRef.current;
    if (!plot) {
      return;
    }
    const colRect = event.currentTarget.getBoundingClientRect();
    const plotRect = plot.getBoundingClientRect();
    setTooltip({
      label: item.label,
      displayLabel,
      value: item.value,
      x: colRect.left - plotRect.left + colRect.width / 2,
    });
  };

  return (
    <section className="analytics-chart-card">
      <div className="analytics-chart-card-header">
        <h3 className="analytics-chart-card-title">{title}</h3>
      </div>
      {data.length === 0 ? (
        <p className="analytics-empty">No data available</p>
      ) : (
        <div className="analytics-chart-body">
          <div
            className="analytics-y-axis"
            aria-hidden
          >
            {yTicks.map(tick => (
              <span
                key={`tick-${tick}`}
                className="analytics-y-tick"
              >
                {tick}
              </span>
            ))}
          </div>
          <div
            className="analytics-plot"
            ref={plotRef}
          >
            {tooltip ? (
              <div
                className="analytics-chart-tooltip-float"
                style={{ left: `${tooltip.x}px` }}
                role="tooltip"
              >
                <strong>{tooltip.displayLabel}</strong>
                <span>Studies: {tooltip.value}</span>
              </div>
            ) : null}
            <div
              className="analytics-grid"
              aria-hidden
            >
              {yTicks.map(tick => (
                <div
                  key={`grid-${tick}`}
                  className="analytics-grid-line"
                />
              ))}
            </div>
            <div className="analytics-line-bars">
              {data.map(item => {
                const displayLabel = modalityDisplayName(item.label);
                return (
                  <div
                    key={item.label}
                    className="analytics-bar-col"
                    onMouseEnter={event => showTooltip(item, displayLabel, event)}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    <span className="analytics-bar-value">{item.value}</span>
                    <div className="analytics-bar-track">
                      <div
                        className="analytics-bar-fill analytics-bar-fill--line"
                        style={{ height: `${Math.max(4, (item.value / tickMax) * 100)}%` }}
                      />
                    </div>
                    <div className="analytics-bar-label-wrap">
                      <span
                        className="analytics-bar-label"
                        title={displayLabel}
                      >
                        {displayLabel}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
